import {
  DROPDOWN_CONFIG,
  SELECTORS,
} from "./LicenseColumnFilterDropdown.constants";

/**
 * Utility functions for LicenseColumnFilterDropdown component
 *
 * This module provides positioning and visibility utilities for dropdown components
 * in data table environments, handling complex scenarios like:
 * - Pinned columns detection and overlap avoidance
 * - Viewport boundary constraints
 * - Dynamic positioning based on available space
 * - Visual hiding detection across different CSS properties
 *
 * Checks if an element is visually hidden by examining computed styles and dimensions
 * @param {HTMLElement} element - The element to check
 * @returns {boolean} - True if the element is visually hidden
 */
export const isElementVisuallyHidden = (element) => {
  if (!element) return true;

  const rect = element.getBoundingClientRect();
  const computedStyle = window.getComputedStyle(element);

  return (
    computedStyle.visibility === "hidden" ||
    computedStyle.opacity === "0" ||
    rect.width === 0 ||
    rect.height === 0
  );
};

/**
 * Checks if an element is positioned under pinned columns or outside visible table area
 * @param {HTMLElement} element - The element to check
 * @returns {boolean} - True if the element is under pinned columns or outside visible area
 */
export const isElementUnderPinnedColumns = (element) => {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  const tableContainer = element.closest(SELECTORS.TABLE_CONTAINERS);

  if (!tableContainer) return false;

  const tableRect = tableContainer.getBoundingClientRect();

  if (rect.left < tableRect.left || rect.right > tableRect.right) {
    return true;
  }

  const checkOverlap = (selector) => {
    const pinnedColumns = document.querySelectorAll(selector);
    return Array.from(pinnedColumns).some((pinnedCol) => {
      const pinnedRect = pinnedCol.getBoundingClientRect();
      return (
        pinnedRect.width > 0 &&
        pinnedRect.height > 0 &&
        rect.right > pinnedRect.left &&
        rect.left < pinnedRect.right &&
        rect.top < pinnedRect.bottom &&
        rect.bottom > pinnedRect.top
      );
    });
  };

  return (
    checkOverlap(SELECTORS.LEFT_PINNED) || checkOverlap(SELECTORS.RIGHT_PINNED)
  );
};

/**
 * Calculates the optimal position for the dropdown based on button element and viewport constraints
 * @param {HTMLElement} buttonElement - The button element that triggers the dropdown
 * @returns {Object} - Object containing top, left, and maxWidth properties for positioning
 */
export const calculateDropdownPosition = (buttonElement) => {
  if (!buttonElement)
    return { top: 0, left: 0, maxWidth: DROPDOWN_CONFIG.DEFAULT_MAX_WIDTH };

  const rect = buttonElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const isRolesDropdown = buttonElement.closest(
    ".license-roles-header-renderer",
  );

  const headerCell = buttonElement.closest(SELECTORS.HEADER_CELL);
  let columnLeft = rect.left;
  let maxDropdownWidth = DROPDOWN_CONFIG.DEFAULT_MAX_WIDTH;

  if (headerCell) {
    const cellRect = headerCell.getBoundingClientRect();
    columnLeft = cellRect.left;
    maxDropdownWidth = Math.min(
      DROPDOWN_CONFIG.CONSTRAINED_MAX_WIDTH,
      cellRect.width - DROPDOWN_CONFIG.COLUMN_PADDING,
    );
  }

  const estimatedDropdownWidth = isRolesDropdown
    ? 230
    : Math.max(
        DROPDOWN_CONFIG.MIN_WIDTH,
        Math.min(maxDropdownWidth, DROPDOWN_CONFIG.PREFERRED_WIDTH),
      );

  let leftPosition = rect.right - estimatedDropdownWidth;

  if (leftPosition < columnLeft) {
    leftPosition = columnLeft;
  }

  if (leftPosition + estimatedDropdownWidth > viewportWidth) {
    leftPosition =
      viewportWidth - estimatedDropdownWidth - DROPDOWN_CONFIG.VIEWPORT_MARGIN;
  }
  if (leftPosition < DROPDOWN_CONFIG.VIEWPORT_MARGIN) {
    leftPosition = DROPDOWN_CONFIG.VIEWPORT_MARGIN;
  }

  return {
    top: rect.bottom + DROPDOWN_CONFIG.TOP_OFFSET,
    left: leftPosition,
    maxWidth: maxDropdownWidth,
  };
};

/**
 * Calculates the optimal position for the company search input box based on the icon element and viewport constraints
 * @param {HTMLElement} iconElement - The icon element that triggers the input box
 * @returns {Object} - Object containing top and left properties for positioning
 */
export const calculateCompanySearchInputPosition = (iconElement) => {
  if (!iconElement) return { top: 0, left: 0 };
  const rect = iconElement.getBoundingClientRect();
  return {
    top: rect.bottom + 4,
    left: rect.right - 160,
  };
};
